import React from "react";
import { TableRow, TableCell, Box } from "@mui/material";
import Moment from "react-moment";
import {
  User,
  AccessTypeCode,
  StatusTypeCode,
  ReinviteUser,
  PinRegenerate,
  EnableUser,
  DisableUser
} from "../../models/userModels";
import { ValueChip, ChipVariantMap, ChipLabelMap } from "../../../../shared/components/chips/ValueChip";
import colors from "../../../../shared/themes/colors";
import PermanentUserActions from "./PermanentUserActions";
import TemporaryAccessActions from "./TemporaryAccessActions";
import { useRoleLabel } from "../../utils/useRoleLabel";

interface EmployeeRowProps {
  user: User;
  variantMap: ChipVariantMap<AccessTypeCode>;
  labelMap: ChipLabelMap<AccessTypeCode>;
  onReinviteUser: (data: ReinviteUser) => void;
  onDeleteUser: (id: string) => void;
  onChangeRole?: (user: User) => void;
  getTimeRemaining: (expirationDate?: Date) => { expired: boolean; text: string } | null;
  getStatusLabel: (status: StatusTypeCode) => string;
  onPinRegenerate: (data: PinRegenerate) => void;
  onEnableTemporaryAccess: (data: EnableUser) => void;
  onDisableTemporaryAccess: (data: DisableUser) => void;
  translate: (key: string, params?: any) => string;
  translateCommon: (key: string, params?: any) => string;
}

export const EmployeeRow: React.FC<EmployeeRowProps> = ({
  user,
  variantMap,
  labelMap,
  onReinviteUser,
  onDeleteUser,
  onChangeRole,
  onPinRegenerate,
  onEnableTemporaryAccess,
  onDisableTemporaryAccess,
  getTimeRemaining,
  getStatusLabel,
  translate,
  translateCommon
}) => {
  const getRoleLabel = useRoleLabel();
  const timeRemaining = getTimeRemaining(user.expirationDate);
  const isExpired = timeRemaining?.expired || false;

  return (
    <TableRow key={user.id}>
      <TableCell className="break-word text-14-regular">
        <Box className="text-14-bold" color={colors.secondary.gray20}>
          {user.firstName} {user.lastName}
        </Box>
        {user.userName}
        {user.role?.name && (
          <Box className="text-12-regular" color={colors.secondary.gray70} sx={{ mt: "2px" }}>
            {getRoleLabel(user.role)}
          </Box>
        )}
      </TableCell>

      <TableCell className="break-word text-14-regular">
        {user.department}
      </TableCell>

      <TableCell className="break-word text-14-regular">
        {user.accessType ? (
          <Box className="flex flex-col gap-2">
            <ValueChip type={user.accessType} variantMap={variantMap} labelMap={labelMap} />
            {user?.visitPurpose}
          </Box>
        ) : null}
      </TableCell>

      <TableCell className="break-word text-14-regular" color={colors.secondary.gray20}>
        {user.loginDate ? <Moment date={user.loginDate} format="DD.MM.YYYY" /> : translate("notJoined")}
      </TableCell>

      <TableCell className="break-word">
        <Box className="flex flex-col">
          <Box
            className="text-14-medium"
            color={isExpired ? colors.primary.red : colors.secondary.gray20}
          >
            {user.expirationDate ? <Moment date={user.expirationDate} format="DD.MM.YYYY HH:mm" /> : "-"}
          </Box>
          {timeRemaining && !isExpired && (
            <Box className="text-14-regular" color={colors.secondary.gray40}>
              {timeRemaining.text}
            </Box>
          )}
        </Box>
      </TableCell>

      <TableCell className="break-word">
        <Box
          className="text-14-regular"
          color={user.status === StatusTypeCode.disabled ? colors.primary.red : colors.secondary.gray20}
        >
          {getStatusLabel(user.status)}
        </Box>
      </TableCell>

      <TableCell>
        {user.accessType === AccessTypeCode.permanent ? (
          <PermanentUserActions
            user={user}
            onReinviteUser={onReinviteUser}
            onDeleteUser={onDeleteUser}
            onChangeRole={onChangeRole}
            translate={translate}
            translateCommon={translateCommon}
          />
        ) : (
          <TemporaryAccessActions
            user={user}
            onPinRegenerate={onPinRegenerate}
            onEnableTemporaryAccess={onEnableTemporaryAccess}
            onDisableTemporaryAccess={onDisableTemporaryAccess}
            onDeleteUser={onDeleteUser}
            translate={translate}
            translateCommon={translateCommon}
          />
        )}
      </TableCell>
    </TableRow>
  );
};
